import type Luminary from '@lumen5/luminary';
import { getGlobalFontManager, TemplateColorProperties } from '@lumen5/luminary';

import { NO_MUSIC_ID, UNREGISTERED } from '../constants';
import type { MusicDescription, TemplateDescription, VideoDescription } from '../types';

import { formatDurationMs } from './assetFormatters';
import { renderColorAsString } from './assetHelpers';

import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { getColorGradingFilterFromUrl } from 'js/src/data/color-grading/color-grading-selectors';
import { getHtmlDesignSystem } from 'js/src/data/html-design';
import { getTemplateColorPalettes } from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';

export function getTemplateFontFamilies(luminary: Luminary): string[] {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Filter and return only the fonts that are actually used in the template
    const fontFamilies = templateFonts
        .map((url) => {
            let font = fontManager.getFontBySource(url);
            if (!font) {
                // Luminary only loads fonts when building the asset
                font = fontManager.addFont(url);
            }
            return font ? font.familyName : null;
        })
        .filter((font) => font !== null);

    return fontFamilies;
}

export function getTemplateFontFamily(luminary: Luminary, fontURL: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested URL
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && url === fontURL) {
            return font.familyName;
        }
    }

    return null; // No matching font found
}

/**
 * The template font slot (0 primary, 1 secondary, 2+ tertiary) for a family name. Indexed against
 * the raw template font list rather than `getTemplateFontFamilies`, which drops fonts that fail to
 * resolve and would shift the remaining slots out of alignment with the template mapping.
 */
export function getTemplateFontIndex(luminary: Luminary, fontFamily: string): number | null {
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    for (let index = 0; index < templateFonts.length; index++) {
        const url = templateFonts[index];
        const font = fontManager.getFontBySource(url) ?? fontManager.addFont(url);
        if (font && font.familyName === fontFamily) {
            return index;
        }
    }

    return null;
}

export function getTemplateFontURL(luminary: Luminary, fontFamily: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested family
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && font.familyName === fontFamily) {
            return url;
        }
    }

    return null; // No matching font found
}

/**
 * The applied color grading filter, named the way set_global_color_grading expects it. Luminary
 * stores the filter as a texture URL, which the agent cannot act on, so it is resolved back to a
 * name through the store. An unresolvable source still reports the raw value rather than claiming
 * nothing is applied.
 */
const getColorGradingDescription = (luminary: Luminary, state: RootState): string => {
    const grading = luminary.template?.grading;
    if (!grading?.source) {
        return 'none';
    }
    const filter = getColorGradingFilterFromUrl(state, grading.source);
    const name = filter?.name ?? grading.source;
    const intensity = grading.intensity ?? 1;
    return intensity < 1 ? `${name} (${Math.round(intensity * 100)}% intensity)` : name;
};

export const getVideoDescription = (luminary: Luminary, state?: RootState): VideoDescription => {
    return {
        resolution: `${luminary.referenceResolution.width}x${luminary.referenceResolution.height}`,
        aspectRatio: luminary.aspectRatio,
        sceneCount: luminary.scenes.length,
        duration: formatDurationMs(luminary.duration),
        // Storeless callers (e.g. the description path) cannot resolve a texture URL to a filter
        // name, so the field is left off rather than reported as an unusable URL.
        ...(state ? { colorGrading: getColorGradingDescription(luminary, state) } : {}),
        ...(state && getHtmlDesignSystem(state)
            ? { designSystem: getHtmlDesignSystem(state)!.look }
            : {}),
    };
};

/**
 * Whether a palette's colors are the ones currently in the active slots — i.e. this palette is the
 * active one. Applying a palette copies its colors into the slots and nothing records which palette
 * that was, so the active palette is recovered by matching the color sequence. Compared by color
 * value in order (alpha can differ per slot via set_template_color); a slot the agent customized
 * shifts the sequence so no palette matches, which correctly reads as "custom".
 *
 * A palette shorter than the active slots isn't copied verbatim: setColorPaletteReferenceOperation
 * pads it by repeating its last color up to the slot count, so the same padding is mirrored here —
 * otherwise a palette the agent just applied would come back unmarked.
 */
const isPaletteActive = (
    paletteColors: number[],
    activeColors: readonly { color: number }[],
): boolean => {
    if (paletteColors.length === 0 || activeColors.length < paletteColors.length) {
        return false;
    }
    const lastPaletteColor = paletteColors[paletteColors.length - 1];
    return activeColors.every((slot, index) =>
        index < paletteColors.length
            ? slot.color === paletteColors[index]
            : slot.color === lastPaletteColor,
    );
};

/**
 * The template's named color presets as a single line the agent can pick from: each palette by name
 * followed by its hex colors, with the currently-active palette marked ` (active)` (e.g.
 * `Corporate (active): #ffffff, #1f3a5b; Warm: #fff8f0, #b5651d`). No palette is marked when the
 * active colors don't match any preset — the slots have been customized. These names are what
 * `set_color_palette` resolves against. Empty string when there's no templates store to read them
 * from (the storeless/minimal-store capture paths) or the template ships no palettes.
 */
const getTemplatePalettesDescription = (
    luminary: Luminary,
    state: RootState | undefined,
    includeColorNames: boolean,
): string => {
    // Palettes live in the templates slice. captureVideoState is store-optional the same way
    // getVideoDescription's colorGrading is — a storeless or minimal-store capture has no templates
    // data, so it reports no palettes. This guards only the presence of the dependency; the palette
    // rendering below is intentionally un-guarded, so a real store with malformed palettes still
    // throws rather than silently rendering nothing. A populated store always resolves palettes, so
    // a real capture is never silently palette-less.
    if (!state?.templates) {
        return '';
    }
    const palettes = getTemplateColorPalettes(state, luminary.template.id);
    const activeColors = luminary.template.colors;
    return palettes
        .map((palette) => {
            const name = palette.get('name');
            const paletteColors = palette.get('colors');
            const active = isPaletteActive(
                paletteColors.map((color) => color.get(TemplateColorProperties.COLOR)).toArray(),
                activeColors,
            );
            const colors = paletteColors
                .map((color) =>
                    renderColorAsString(
                        color.get(TemplateColorProperties.COLOR),
                        includeColorNames,
                    ),
                )
                .join(', ');
            return `${name}${active ? ' (active)' : ''}: ${colors}`;
        })
        .join('; ');
};

/**
 * Get template metadata including ID, fonts, active colors, and named palettes.
 * @param luminary The Luminary instance containing template data
 * @param includeColorNames Whether palette colors carry a `(name)` alongside the hex; off for the
 *   Toolbox, whose model reads hex directly.
 * @param state Redux state, needed to resolve the template's named palettes. Storeless callers get
 *   an empty `templatePalettes`.
 * @returns Object with template ID, font families, active color slots, and named palettes
 */
export const getTemplateDescription = (
    luminary: Luminary,
    includeColorNames: boolean = true,
    state?: RootState,
): TemplateDescription => {
    return {
        template: luminary.template.id,
        templateFonts: getTemplateFontFamilies(luminary).join(', '),
        // Slot indices are addressable — set_template_color takes one to set a single active color.
        templateColors: luminary.template.colors
            .map(
                (item, index) => `[${index}] ${renderColorAsString(item.color, includeColorNames)}`,
            )
            .join(', '),
        templatePalettes: getTemplatePalettesDescription(luminary, state, includeColorNames),
    };
};

export const getVideoMusicDescription = (luminary: Luminary): MusicDescription => {
    // A track is set whenever luminary.music carries a uuid. Report the registry id rather than
    // the asset uuid: `set_music` takes a music_id, so the uuid would be an identifier the agent
    // cannot act on. A set-but-unregistered track (e.g. a template default the agent hasn't
    // searched up) reports UNREGISTERED, keeping it distinct from a video with no music at all
    // rather than both collapsing to the same "No music set" placeholder.
    if (luminary.music?.uuid) {
        const musicRegistry = MusicRegistry.getInstance();
        const musicId = musicRegistry.getItemIdByUuid(luminary.music.uuid);
        const musicRepresentation = musicId ? musicRegistry.getRepresentation(musicId) : undefined;
        return {
            id: musicId ?? UNREGISTERED,
            volume: luminary.musicAsset?.volume ?? 0,
            title: musicRepresentation
                ? musicRepresentation.title || 'Untitled'
                : 'Music set (not in registry)',
        };
    }
    return {
        id: NO_MUSIC_ID,
        volume: 0,
        title: 'No music set',
    };
};
